import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import rolesService from '../../service/api/RolesService';
import usersService from '../../service/api/UsersService';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import UserDialog from './UserDialog';
import { Trans, useTranslation } from 'react-i18next';
import divisionsService from '../../service/api/DivisionsService';
import { User } from '../../types/User';
import { Role } from '../../types/Role';
import { Division } from '../../types/Division';

const emptyUser = {
  id: null,
};

export const UserManagement = () => {
  const [users, setUsers] = useState<User[]>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User>();
  const [roles, setUserRoles] = useState<Role[]>();
  const [divisions, setDivisions] = useState<Division[]>();
  const [deleteUserDialogVisible, setDeleteUserDialogVisible] = useState(false);
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<User>(null);
  const [userDialogVisible, setUserDialogVisible] = useState(false);
  const loggedUserContext = useSelector((state: any) => state.user.context);
  const { t } = useTranslation();

  const loadData = () => {
    setLoading(true);
    Promise.all([usersService.get(), rolesService.getAll(), divisionsService.getAll()]).then((response) => {
      setUserRoles(response[1]);
      setDivisions(response[2]);
      const rolesDict = Object.assign({}, ...response[1].map((r) => ({ [r.id]: r.name })));
      const divisionsDict = Object.assign({}, ...response[2].map((r) => ({ [r.id]: r.name })));
      const users = response[0];
      users.forEach((u) => {
        u.roleName = rolesDict[u.roleId];
        u.divisionName = divisionsDict[u.divisionId];
      });
      setUsers(users);

      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const usersTableHeader = (
    <div className="table-header">
      <h3 className="m-0">{t('users.header')}</h3>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={() => {
            addUser();
          }}
        ></Button>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setUserDialogVisible(false);
    loadData();
  };

  const confirmDeleteUser = (user: User) => {
    setUser(user);
    setDeleteUserDialogVisible(true);
  };

  const deleteUser = async () => {
    setLoading(true);
    await usersService.delete(user.id);

    setDeleteUserDialogVisible(false);
    setUser(emptyUser as User);
  };
  const hideDeleteUserDialog = () => {
    setDeleteUserDialogVisible(false);
  };

  const editUser = (_user: User) => {
    setUser(_user);
    setSelectedRowToEdit(_user);
    setUserDialogVisible(true);
  };

  const addUser = () => {
    setUser(emptyUser as User);
    setSelectedRowToEdit(emptyUser as User);
    setUserDialogVisible(true);
  };

  const deleteUserDialogFooter = (
    <>
      <Button
        label={t('common.no')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => hideDeleteUserDialog()}
      />
      <Button label={t('common.yes')} icon="pi pi-check" className="p-button-text" onClick={() => deleteUser()} />
    </>
  );

  const bodyTemplate = (data: User, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const activeBodyTemplate = (data: User) => {
    return (
      <React.Fragment>
        {data.active === true ? (
          <Tag className="mr-2" icon="pi pi-check" severity="success" value={t('common.active')}></Tag>
        ) : (
          <Tag className="mr-2" icon="pi pi-times-circle" severity="danger" value={t('common.inactive')}></Tag>
        )}
      </React.Fragment>
    );
  };

  const actionTemplate = (data: User) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          editUser(data);
        }}
      ></Button>
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success"
        disabled={loggedUserContext.id === data.id ? true : undefined}
        onClick={() => {
          confirmDeleteUser(data);
        }}
      ></Button>
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={users}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('users.users_empty_message')}
            loading={loading}
            header={usersTableHeader}
          >
            <Column field="login" header={t('users.login')} sortable body={bodyTemplate}></Column>
            <Column field="firstName" header={t('users.first_name')} sortable body={bodyTemplate}></Column>
            <Column field="lastName" header={t('users.last_name')} sortable body={bodyTemplate}></Column>
            <Column field="roleName" header={t('users.role')} sortable body={bodyTemplate}></Column>
            <Column field="divisionName" header={t('users.division')} sortable body={bodyTemplate}></Column>
            <Column
              field="active"
              header={t('common.active')}
              sortable
              body={activeBodyTemplate}
              align="center"
            ></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <UserDialog
            userDialogVisible={userDialogVisible}
            closeAddEditModal={closeAddEditModal}
            user={selectedRowToEdit}
            availableRoles={roles}
            availableDivisions={divisions}
          />
          <Dialog
            visible={deleteUserDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteUserDialogFooter}
            onHide={hideDeleteUserDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {user && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: user.login }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
